"use client";

import { useEffect, useLayoutEffect, useRef, type ReactNode } from "react";

/** History restoration can commit outside React's animated route transition. */
export function CcHistoryPageTransition({ pathname, children }: { pathname: string; children: ReactNode }) {
  const root = useRef<HTMLDivElement>(null);
  const currentPath = useRef(pathname);
  const historyTarget = useRef<string | null>(null);
  const animation = useRef<Animation | null>(null);

  useEffect(() => {
    const onHistory = () => {
      const target = window.location.pathname;
      historyTarget.current = target !== currentPath.current ? target : null;
    };
    window.addEventListener("popstate", onHistory);
    return () => {
      window.removeEventListener("popstate", onHistory);
      animation.current?.cancel();
    };
  }, []);

  useLayoutEffect(() => {
    currentPath.current = pathname;
    const target = historyTarget.current;
    if (!target || target !== pathname) return;
    historyTarget.current = null;
    animation.current?.cancel();
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const node = root.current;
    if (!node?.animate) return;
    const mobile = window.matchMedia("(max-width: 809px)").matches;
    animation.current = node.animate([
      { opacity: 0, transform: `translateY(${mobile ? 10 : 16}px)` },
      { opacity: 1, transform: "translateY(0)" },
    ], { duration: mobile ? 180 : 220, easing: "cubic-bezier(0.22, 1, 0.36, 1)" });
  }, [pathname]);

  return <div ref={root} className="cc-history-page-transition">{children}</div>;
}
